import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DestroyRef, ElementRef, TemplateRef, ViewContainerRef, signal } from '@angular/core';
import { filter } from 'rxjs';

const POSITIONS: ConnectedPosition[] = [
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
  { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
];

/** Above the trigger first, for triggers near the bottom of the window (the footer). */
export const SHELL_POPOVER_ABOVE: ConnectedPosition[] = [POSITIONS[1], POSITIONS[0]];

export interface ShellPopoverHost {
  overlay: Overlay;
  viewContainerRef: ViewContainerRef;
  destroyRef: DestroyRef;
  trigger: () => ElementRef<HTMLElement>;
  template: () => TemplateRef<unknown>;
  /** Defaults to under the trigger, end-aligned, flipping above when there's no room. */
  positions?: ConnectedPosition[];
}

/**
 * A shell popover anchored to its trigger (under it by default). Escape
 * closes it and returns focus to the trigger; a click outside closes it and
 * leaves focus alone.
 */
export class ShellPopover {
  readonly isOpen = signal(false);
  private overlayRef: OverlayRef | null = null;

  constructor(private readonly host: ShellPopoverHost) {
    host.destroyRef.onDestroy(() => this.overlayRef?.dispose());
  }

  /** The panel element while open. */
  get panel(): HTMLElement | null {
    return this.overlayRef?.overlayElement ?? null;
  }

  toggle(onOpen?: (panel: HTMLElement) => void): void {
    if (this.isOpen()) this.close(true);
    else this.open(onOpen);
  }

  open(onOpen?: (panel: HTMLElement) => void): void {
    if (this.isOpen()) return;
    const ref = this.ensureOverlay();
    ref.attach(new TemplatePortal(this.host.template(), this.host.viewContainerRef));
    this.isOpen.set(true);
    // Skipped if the panel closed or went away before the next frame.
    if (onOpen) requestAnimationFrame(() => this.isOpen() && ref.overlayElement && onOpen(ref.overlayElement));
  }

  close(returnFocus: boolean): void {
    if (!this.isOpen()) return;
    this.overlayRef?.detach();
    this.isOpen.set(false);
    if (returnFocus) this.host.trigger().nativeElement.focus();
  }

  private ensureOverlay(): OverlayRef {
    if (this.overlayRef) return this.overlayRef;
    const { overlay } = this.host;
    const positionStrategy = overlay
      .position()
      .flexibleConnectedTo(this.host.trigger())
      .withPositions(this.host.positions ?? POSITIONS)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(8);
    const ref = overlay.create({
      positionStrategy,
      scrollStrategy: overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });
    ref.backdropClick().subscribe(() => this.close(false));
    ref
      .keydownEvents()
      .pipe(filter((e) => e.key === 'Escape'))
      .subscribe((e) => {
        e.preventDefault();
        this.close(true);
      });
    ref.detachments().subscribe(() => this.isOpen.set(false));
    this.overlayRef = ref;
    return ref;
  }
}
